import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

const DATE_LOCALE = {
  de: "de-DE",
  en: "en-GB",
  zh: "zh-CN",
};

function Downloads() {
  const { t, i18n } = useTranslation();
  const base = process.env.PUBLIC_URL || "";
  const dateLocale =
    DATE_LOCALE[i18n.language?.split("-")[0]] || DATE_LOCALE.en;

  const formatFileSize = useCallback((bytes) => {
    if (!bytes || Number.isNaN(bytes) || bytes === 0) return null;
    const n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
    if (Number.isNaN(n) || n === 0) return null;
    const mb = n / (1024 * 1024);
    if (mb < 0.1) {
      const kb = n / 1024;
      return `${kb.toFixed(1)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  }, []);

  const fetchFileInfoWithGet = useCallback(
    async (url) => {
      try {
        const response = await fetch(url, { method: "GET" });
        if (!response.ok) {
          return { date: null, size: null };
        }

        let date = null;
        const lastModifiedHeader = response.headers.get("last-modified");
        if (lastModifiedHeader) {
          try {
            const lastModified = new Date(lastModifiedHeader);
            if (!Number.isNaN(lastModified.getTime())) {
              date = lastModified.toLocaleDateString(dateLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
            }
          } catch {
            /* ignore */
          }
        }

        const contentLengthHeader = response.headers.get("content-length");
        let size = null;
        if (contentLengthHeader) {
          const contentLength = parseInt(contentLengthHeader, 10);
          if (!Number.isNaN(contentLength) && contentLength > 0) {
            size = formatFileSize(contentLength);
          }
        }

        if (size == null) {
          const blob = await response.blob();
          if (blob?.size > 0) {
            size = formatFileSize(blob.size);
          }
        }

        return { date, size };
      } catch (error) {
        console.error("Downloads: GET file info failed", error);
        return { date: null, size: null };
      }
    },
    [dateLocale, formatFileSize]
  );

  const fetchFileInfo = useCallback(
    async (url) => {
      try {
        const response = await fetch(url, { method: "HEAD" });
        if (!response.ok) {
          return fetchFileInfoWithGet(url);
        }

        let date = null;
        const lastModifiedHeader = response.headers.get("last-modified");
        if (lastModifiedHeader) {
          try {
            const lastModified = new Date(lastModifiedHeader);
            if (!Number.isNaN(lastModified.getTime())) {
              date = lastModified.toLocaleDateString(dateLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
            }
          } catch {
            /* ignore */
          }
        }

        const contentLengthHeader = response.headers.get("content-length");
        let size = null;
        if (contentLengthHeader) {
          const contentLength = parseInt(contentLengthHeader, 10);
          if (!Number.isNaN(contentLength) && contentLength > 0) {
            size = formatFileSize(contentLength);
          }
        }

        if (size == null || date == null) {
          const getInfo = await fetchFileInfoWithGet(url);
          if (size == null) size = getInfo.size;
          if (date == null) date = getInfo.date;
        }

        return { date, size };
      } catch (error) {
        console.error("Downloads: HEAD file info failed", error);
        try {
          return await fetchFileInfoWithGet(url);
        } catch {
          return { date: null, size: null };
        }
      }
    },
    [dateLocale, fetchFileInfoWithGet, formatFileSize]
  );

  const downloadItems = useMemo(
    () => [
      {
        id: "tender",
        icon: "fas fa-file-pdf",
        title: t("downloads.tenderTitle"),
        description: t("downloads.tenderDescription"),
        downloadUrl:
          i18n.language === "zh"
            ? "/documents/第十八届世界精武武术锦标赛暨文化大会.pdf"
            : "/documents/18th_World_Chin_Woo_Martial_Arts_Championship_&_Culture_Assembly.pdf",
        fileName:
          i18n.language === "zh"
            ? "第十八届世界精武武术锦标赛暨文化大会.pdf"
            : "18th_World_Chin_Woo_Martial_Arts_Championship_&_Culture_Assembly.pdf",
        docLang: i18n.language === "zh" ? "zh" : "en",
      },
      {
        id: "invitation",
        icon: "fas fa-file-pdf",
        title: t("downloads.invitationTitle"),
        description: t("downloads.invitationDescription"),
        downloadUrl:
          i18n.language === "zh"
            ? "/documents/第十八届世界精武武术文化大会_邀请函.pdf"
            : "/documents/Invitation_Wolrd_Chin_Woo_Convention_2026.pdf",
        fileName:
          i18n.language === "zh"
            ? "第十八届世界精武武术文化大会_邀请函.pdf"
            : "Invitation_Wolrd_Chin_Woo_Convention_2026.pdf",
        docLang: i18n.language === "zh" ? "zh" : "en",
      },
      {
        id: "eventmap",
        icon: "fas fa-file-pdf",
        title: t("sponsoringBenefits.downloadEventmappe"),
        description: t("downloads.eventmapDescription"),
        downloadUrl: "/documents/Event-Mappe.pdf",
        fileName: "GCWAA-Event-Mappe.pdf",
        docLang: "de",
      },
    ],
    [i18n.language, t]
  );

  const [fileInfo, setFileInfo] = useState({});
  const [loading, setLoading] = useState(true);
  /** Nur eine Vorschau gleichzeitig offen (Performance, Übersicht). */
  const [openPreviewId, setOpenPreviewId] = useState(null);

  const togglePreview = useCallback((id) => {
    setOpenPreviewId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const info = {};
      for (const item of downloadItems) {
        if (cancelled) return;
        info[item.downloadUrl] = await fetchFileInfo(`${base}${item.downloadUrl}`);
      }
      if (!cancelled) {
        setFileInfo(info);
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [downloadItems, fetchFileInfo, base]);

  const flagSrc = (lang) => {
    const map = {
      zh: "/images/flags/CN.png",
      en: "/images/flags/GB.png",
      de: "/images/flags/DE.png",
    };
    return `${base}${map[lang] || map.en}`;
  };

  const langCodeLabel = (lang) =>
    ({ zh: "ZH", en: "EN", de: "DE" }[lang] || "EN");

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="downloads"
      aria-labelledby="downloads-page-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h1
            id="downloads-page-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("downloads.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("downloads.description")}
          </p>
        </header>

        {loading ? (
          <div
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
            aria-busy="true"
            aria-label={t("downloads.loading_label")}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="aspect-[5/3] bg-slate-200 dark:bg-slate-700" />
                <div className="space-y-3 p-6">
                  <div className="h-5 w-3/4 rounded-lg bg-slate-200 dark:bg-slate-700" />
                  <div className="h-4 w-full rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-4 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-10 w-full rounded-xl bg-slate-200 dark:bg-slate-700" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {downloadItems.map((item) => {
              const href = `${base}${item.downloadUrl}`;
              const meta = fileInfo[item.downloadUrl] || {};
              const dateStr = meta.date ?? t("downloads.meta_not_available");
              const sizeStr = meta.size ?? t("downloads.meta_not_available");
              const previewOpen = openPreviewId === item.id;
              const previewPanelId = `download-preview-${item.id}`;

              return (
                <li key={item.id} className="min-h-0">
                  <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm ring-1 ring-slate-200/50 transition-all dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800 dark:ring-slate-700/60 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md motion-safe:group-hover:ring-primary/15 dark:motion-safe:group-hover:ring-primary-light/20">
                    <div className="relative aspect-[5/3] bg-gradient-to-br from-slate-100 to-slate-200/80 dark:from-slate-700/40 dark:to-slate-800/80">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 sm:h-24 sm:w-24 motion-safe:group-hover:scale-[1.02]"
                          aria-hidden
                        >
                          <i
                            className={`${item.icon} text-3xl text-primary dark:text-primary-light sm:text-4xl`}
                          />
                        </div>
                      </div>
                      <div
                        className="absolute right-3 top-3 flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/95 py-1 pl-1 pr-2.5 shadow-sm dark:border-slate-600 dark:bg-slate-800/95"
                        aria-label={t(`downloads.lang_${item.docLang}`)}
                      >
                        <img
                          src={flagSrc(item.docLang)}
                          alt=""
                          className="h-7 w-7 rounded-full object-cover ring-2 ring-white dark:ring-slate-800"
                          width={28}
                          height={28}
                        />
                        <span
                          className="text-xs font-semibold tabular-nums text-slate-600 dark:text-slate-300"
                          aria-hidden
                        >
                          {langCodeLabel(item.docLang)}
                        </span>
                      </div>
                    </div>

                    <div className="border-b border-slate-100 dark:border-slate-700/80">
                      <button
                        type="button"
                        onClick={() => togglePreview(item.id)}
                        className="flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-slate-50 dark:text-primary-light dark:hover:bg-slate-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35 dark:focus-visible:ring-primary-light/35"
                        aria-expanded={previewOpen}
                        aria-controls={previewPanelId}
                      >
                        <i
                          className={`fas fa-eye${previewOpen ? "-slash" : ""} text-xs opacity-90`}
                          aria-hidden
                        />
                        {previewOpen
                          ? t("downloads.preview_hide")
                          : t("downloads.preview_show")}
                        <i
                          className={`fas fa-chevron-${previewOpen ? "up" : "down"} text-[10px] opacity-70`}
                          aria-hidden
                        />
                      </button>
                      <div
                        id={previewPanelId}
                        role="region"
                        hidden={!previewOpen}
                        className="border-t border-slate-200 bg-slate-100/80 dark:border-slate-600 dark:bg-slate-900/60"
                      >
                        {previewOpen ? (
                          <div className="h-56 w-full sm:h-64 md:h-72">
                            <iframe
                              src={`${href}#view=FitH&embedded=true&toolbar=0&navpanes=0&scrollbar=0&pagemode=none`}
                              title={t("downloads.preview_iframe_title", {
                                title: item.title,
                              })}
                              className="h-full w-full border-0"
                              loading="lazy"
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-5 sm:p-6">
                      <h2 className="text-lg font-semibold leading-snug text-slate-900 dark:text-slate-100">
                        {item.title}
                      </h2>
                      <p className="mt-2 flex-1 text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                        {item.description}
                      </p>

                      <div className="mt-4 flex flex-wrap items-center text-xs text-slate-500 dark:text-slate-400">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="sr-only">{t("downloads.meta_date_label")}: </span>
                          <i
                            className="fas fa-calendar-alt shrink-0 text-slate-400 dark:text-slate-500"
                            aria-hidden
                          />
                          <span className="tabular-nums">{dateStr}</span>
                        </span>
                        <span
                          className="mx-3 h-4 w-px shrink-0 self-center bg-slate-200 dark:bg-slate-600"
                          aria-hidden
                        />
                        <span className="inline-flex items-center gap-1.5">
                          <span className="sr-only">{t("downloads.meta_size_label")}: </span>
                          <i
                            className="fas fa-file shrink-0 text-slate-400 dark:text-slate-500"
                            aria-hidden
                          />
                          <span>{sizeStr}</span>
                        </span>
                      </div>

                      <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 dark:border-slate-700/80 sm:flex-row sm:items-stretch">
                        <a
                          href={href}
                          download={item.fileName}
                          className="inline-flex min-h-[2.75rem] flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-center text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark dark:bg-primary-light dark:text-slate-900 dark:hover:bg-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/50 dark:focus-visible:ring-offset-slate-900"
                          aria-label={t("downloads.download_aria", {
                            title: item.title,
                          })}
                        >
                          <i className="fas fa-download text-sm opacity-90" aria-hidden />
                          {t("downloads.downloadButton")}
                        </a>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-[2.75rem] flex-1 items-center justify-center gap-2 rounded-xl border-2 border-primary bg-white px-4 py-2.5 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/5 dark:border-primary-light dark:bg-slate-800 dark:text-primary-light dark:hover:bg-primary-light/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-900"
                          aria-label={t("downloads.open_pdf_aria", {
                            title: item.title,
                          })}
                        >
                          {t("downloads.open_pdf")}
                          <i
                            className="fas fa-external-link-alt text-xs opacity-80"
                            aria-hidden
                          />
                        </a>
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default Downloads;
